import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Bell,
  Calculator,
  ClipboardList,
  FileText,
  Headphones,
  History,
  LayoutTemplate,
  ListChecks,
  Repeat,
  Send,
  Settings,
  ShieldAlert,
  SlidersHorizontal,
  Ticket,
  Wallet,
} from "lucide-react";
import { useAccess } from "../access/hooks/useAccess";
import { translations } from "../utils/lang";
import { TEXT as NOTIFICATION_TEXT } from "../components/ludNotifications/utils/notificationHelpers";
import {
  ALL_SECTIONS as GUIDELINE_SECTIONS,
  BRANCH_SECTIONS as GUIDELINE_BRANCH_SECTIONS,
} from "../components/guidlines/guidelineSections";

/*
  Sections of pages that are opened from sidebar submenus. Keyed by the route
  id (src/access/core/routePolicies.js); the selected section lives in the
  page's query string (`?<param>=<key>`) so refresh, direct links and
  back/forward keep it.

  section.label(lang)  sidebar label
  section.visible      optional ({ can, has }) => boolean; hidden sections are
                       left out of the sidebar AND unreachable by URL (the
                       page falls back to its first visible section)

  Pages read/write the selection with usePageSection(routeId).
*/

const billingAdmin = ({ can }) => can("billing.admin.view");
const billingLabel = (key) => (lang) =>
  (translations[lang] || translations.en).billing.admin.shell.tabs[key];

const canSendNotifications = ({ has }) => has("notification.send");

export const PAGE_SECTIONS = {
  guidelines: {
    param: "section",
    sections: GUIDELINE_SECTIONS.map(({ key, icon, label }) => ({
      key,
      Icon: icon,
      label: (lang) => label[lang === "ar" ? "ar" : "en"],
      // Branch users only get the branch list (Videos).
      visible: ({ can }) =>
        !can("guideline.section.branchOnly") ||
        GUIDELINE_BRANCH_SECTIONS.some((section) => section.key === key),
    })),
  },
  billing: {
    param: "tab",
    sections: [
      { key: "invoices", Icon: FileText, label: billingLabel("invoices"), visible: billingAdmin },
      { key: "subscriptions", Icon: Repeat, label: billingLabel("subscriptions"), visible: billingAdmin },
      { key: "followUp", Icon: ListChecks, label: billingLabel("followUp"), visible: billingAdmin },
      {
        key: "accountant",
        Icon: Calculator,
        label: (lang) => (lang === "en" ? "Accounting" : "المحاسبة"),
        visible: ({ can }) => can("billing.accountant.view"),
      },
      {
        key: "aiWallets",
        Icon: Wallet,
        label: (lang) => (lang === "en" ? "AI Wallets" : "محافظ الذكاء الاصطناعي"),
        visible: billingAdmin,
      },
      { key: "settings", Icon: Settings, label: billingLabel("settings"), visible: billingAdmin },
    ],
  },
  "delivery-documentation-admin": {
    param: "tab",
    sections: [
      { key: "documentation", Icon: ClipboardList, label: (lang) => (lang === "en" ? "Documentation log" : "سجل التوثيق") },
      { key: "disputes", Icon: ShieldAlert, label: (lang) => (lang === "en" ? "Disputes" : "الاعتراضات") },
      { key: "dispute-settings", Icon: SlidersHorizontal, label: (lang) => (lang === "en" ? "Dispute settings" : "إعدادات الاعتراض") },
      { key: "logs", Icon: History, label: (lang) => (lang === "en" ? "Logs" : "السجلات") },
    ],
  },
  notifications: {
    param: "tab",
    sections: [
      { key: "inbox", Icon: Bell, label: (lang) => (NOTIFICATION_TEXT[lang] || NOTIFICATION_TEXT.ar).tabs.inbox },
      {
        key: "send",
        Icon: Send,
        label: (lang) => (NOTIFICATION_TEXT[lang] || NOTIFICATION_TEXT.ar).tabs.send,
        visible: canSendNotifications,
      },
      {
        key: "builder",
        Icon: LayoutTemplate,
        label: (lang) => (NOTIFICATION_TEXT[lang] || NOTIFICATION_TEXT.ar).tabs.builder,
        visible: (access) => canSendNotifications(access) && access.can("notification.template.manage"),
      },
    ],
  },
  support: {
    param: "tab",
    sections: [
      { key: "support", Icon: Headphones, label: (lang) => (lang === "en" ? "Support" : "الدعم الفني") },
      { key: "tickets", Icon: Ticket, label: (lang) => (lang === "en" ? "Tickets" : "التذاكر") },
      {
        key: "category",
        Icon: Settings,
        label: (lang) => (lang === "en" ? "Categories" : "الاقسام"),
        visible: ({ can }) => can("support.category.manage"),
      },
    ],
  },
};

export const visibleSections = (routeId, access) =>
  (PAGE_SECTIONS[routeId]?.sections || []).filter(
    (section) => !section.visible || section.visible(access),
  );

// Unknown, missing or unauthorized values fall back to the first visible section.
export const resolveSection = (routeId, value, access) => {
  const sections = visibleSections(routeId, access);
  if (sections.some((section) => section.key === value)) return value;
  return (sections[0] || PAGE_SECTIONS[routeId].sections[0]).key;
};

export function usePageSection(routeId) {
  const [params, setParams] = useSearchParams();
  const access = useAccess();
  const { param } = PAGE_SECTIONS[routeId];
  const active = resolveSection(routeId, params.get(param), access);

  const setSection = useCallback(
    (key) => {
      const next = new URLSearchParams(params);
      next.set(param, key);
      setParams(next);
    },
    [params, param, setParams],
  );

  return [active, setSection];
}
